import type { SkinDocument, SkinModel } from "./types";
import { importSkinPng } from "./png";

const PLAYER_LOOKUP_ENDPOINT = "https://playerdb.co/api/player/minecraft/";
const MINECRAFT_TEXTURE_ORIGIN = "https://textures.minecraft.net";
const PLAYER_NAME_PATTERN = /^[A-Za-z0-9_]{1,16}$/;
const UUID_PATTERN =
  /^(?:[0-9a-fA-F]{32}|[0-9a-fA-F]{8}(?:-[0-9a-fA-F]{4}){3}-[0-9a-fA-F]{12})$/;

interface PlayerDbProfile {
  username: string;
  skinTexture: string;
  properties: Array<{ name: string; value: string }>;
}

export interface JavaPlayerSkin {
  document: SkinDocument;
  username: string;
}

export class JavaPlayerSkinError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JavaPlayerSkinError";
  }
}

export async function fetchJavaPlayerSkin(
  playerId: string,
  request: typeof fetch = fetch,
): Promise<JavaPlayerSkin> {
  const normalizedId = playerId.trim();
  if (!normalizedId) {
    throw new JavaPlayerSkinError("请输入 Java 版玩家 ID。");
  }
  if (
    !PLAYER_NAME_PATTERN.test(normalizedId) &&
    !UUID_PATTERN.test(normalizedId)
  ) {
    throw new JavaPlayerSkinError(
      "玩家 ID 应为 1–16 位英文、数字或下划线，或有效 UUID。",
    );
  }

  const profileResponse = await fetchResponse(
    request,
    `${PLAYER_LOOKUP_ENDPOINT}${encodeURIComponent(normalizedId)}`,
    "application/json",
    "无法连接玩家资料服务，请检查网络后重试。",
  );
  const profilePayload = await readJson(profileResponse);
  if (!profileResponse.ok) {
    throw profileLookupError(profileResponse.status, profilePayload);
  }
  const profile = parsePlayerProfile(profilePayload);
  const textureUrl = parseOfficialTextureUrl(profile.skinTexture);
  const model = readProfileModel(profile.properties);

  const textureResponse = await fetchResponse(
    request,
    textureUrl,
    "image/png",
    "无法下载该玩家的皮肤，请检查网络后重试。",
  );
  if (!textureResponse.ok) {
    throw new JavaPlayerSkinError(
      textureResponse.status === 404
        ? "该玩家当前没有可用的皮肤纹理。"
        : "Minecraft 皮肤纹理服务暂时不可用，请稍后重试。",
    );
  }
  const contentType = textureResponse.headers.get("content-type") ?? "";
  if (!contentType.toLowerCase().startsWith("image/png")) {
    throw new JavaPlayerSkinError("皮肤纹理服务返回了无效的文件格式。");
  }

  const document = await importSkinPng(await textureResponse.blob(), model);
  return { document, username: profile.username };
}

async function fetchResponse(
  request: typeof fetch,
  url: string,
  accept: string,
  networkMessage: string,
): Promise<Response> {
  try {
    return await request(url, {
      headers: { Accept: accept },
    });
  } catch {
    throw new JavaPlayerSkinError(networkMessage);
  }
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    if (!response.ok) return undefined;
    throw new JavaPlayerSkinError("玩家资料服务返回了无效数据。");
  }
}

function profileLookupError(
  status: number,
  payload: unknown,
): JavaPlayerSkinError {
  const code =
    isRecord(payload) && typeof payload.code === "string"
      ? payload.code.toLowerCase()
      : "";
  if (
    status === 400 ||
    status === 404 ||
    code.includes("invalid") ||
    code.includes("not_found") ||
    code.includes("not-found")
  ) {
    return new JavaPlayerSkinError("未找到这个 Java 版玩家。");
  }
  if (status === 429) {
    return new JavaPlayerSkinError("查询过于频繁，请稍后再试。");
  }
  return new JavaPlayerSkinError("玩家资料服务暂时不可用，请稍后重试。");
}

function parsePlayerProfile(payload: unknown): PlayerDbProfile {
  if (
    !isRecord(payload) ||
    payload.success !== true ||
    !isRecord(payload.data) ||
    !isRecord(payload.data.player)
  ) {
    throw new JavaPlayerSkinError("玩家资料服务返回了无效数据。");
  }
  const player = payload.data.player;
  if (
    typeof player.username !== "string" ||
    typeof player.skin_texture !== "string" ||
    !Array.isArray(player.properties)
  ) {
    throw new JavaPlayerSkinError("玩家资料中缺少皮肤纹理。");
  }
  const properties = player.properties.flatMap((property) =>
    isRecord(property) &&
    typeof property.name === "string" &&
    typeof property.value === "string"
      ? [{ name: property.name, value: property.value }]
      : [],
  );
  return {
    username: player.username,
    skinTexture: player.skin_texture,
    properties,
  };
}

function parseOfficialTextureUrl(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new JavaPlayerSkinError("玩家资料中的皮肤纹理地址无效。");
  }
  if (
    url.origin !== MINECRAFT_TEXTURE_ORIGIN ||
    !/^\/texture\/[0-9a-f]+$/i.test(url.pathname) ||
    url.search ||
    url.hash
  ) {
    throw new JavaPlayerSkinError("玩家资料中的皮肤纹理地址不受信任。");
  }
  return url.href;
}

function readProfileModel(
  properties: PlayerDbProfile["properties"],
): SkinModel | undefined {
  const textures = properties.find((property) => property.name === "textures");
  if (!textures) return undefined;
  try {
    const decoded = JSON.parse(decodeBase64(textures.value)) as unknown;
    if (
      isRecord(decoded) &&
      isRecord(decoded.textures) &&
      isRecord(decoded.textures.SKIN) &&
      isRecord(decoded.textures.SKIN.metadata) &&
      decoded.textures.SKIN.metadata.model === "slim"
    ) {
      return "slim";
    }
    return "classic";
  } catch {
    return undefined;
  }
}

function decodeBase64(value: string): string {
  if (value.length > 1_000_000) {
    throw new Error("Base64 payload is too large.");
  }
  return atob(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
